import { getTechnicianToken } from "@/services/technician-app.service";
import { GpsBootLoader, RouteProgressBar } from "@/components/gps-loader";
import { QueryClient, defaultShouldDehydrateQuery } from "@tanstack/react-query";
import { ForcePasswordChange } from "@/components/force-password-change";
import { PersistQueryClientProvider } from "@tanstack/react-query-persist-client";
import { gpsDrivePersister } from "@/lib/query-persister";
import {
  Outlet,
  createRootRouteWithContext,
  useNavigate,
  useRouter,
  useRouterState,
  HeadContent,
  Scripts,
} from "@tanstack/react-router";
import { useEffect, useState, type ReactNode } from "react";
import { Loader2 } from "lucide-react";

import appCss from "../styles.css?url";
import { reportLovableError } from "../lib/lovable-error-reporting";
import { AppSidebar, SIDEBAR_ITEMS, useSidebarNavItems } from "@/components/app-sidebar";
import { MobileApp } from "@/components/mobile-app";
import { FuncionarioMobileShell } from "@/components/funcionario-mobile-shell";
import { Toaster } from "@/components/ui/sonner";
import { AuthProvider, useAuth } from "@/contexts/auth-context";
import { PlatformAuthProvider } from "@/contexts/platform-auth-context";
import { useRealtimeSync } from "@/hooks/use-realtime-sync";
import { useLiveSync } from "@/hooks/use-live-sync";
import { useNotificationSoundPlayer } from "@/hooks/use-notification-sound-player";
import { useNativePushRegistration } from "@/hooks/use-native-push-registration";
import { useNativeNotificationActions } from "@/hooks/use-native-notification-actions";
import { useBrandingTheme, applyCachedAppIcon } from "@/hooks/use-branding-theme";
import { useMyCompany, useMyPermissions } from "@/hooks/queries";
import { UpdateAvailableNotice } from "@/components/update-available-notice";
import { ConnectionLostBanner } from "@/components/connection-lost-banner";
import { AppStoreUpdatePrompt } from "@/components/app-store-update-prompt";
import { cn } from "@/lib/utils";
import { registerAssetCache } from "@/lib/asset-cache";

if (typeof window !== "undefined") applyCachedAppIcon();

// Aplica a identidade visual (cor primária / favicon) configurada em
// Administração — montado como componente próprio (não uma chamada
// condicional do hook) pra só rodar depois que o AuthGate confirma sessão
// válida, evitando um GET /admin/branding sem token na tela de login.
function BrandingThemeMount() {
  useBrandingTheme();
  return null;
}

// Esquenta o cache de ["my-permissions"] assim que a sessão é confirmada,
// não só quando a primeira tela que precisa dela (ex.: painel expandido de
// Usuários, botão "Logar como") monta. useMyPermissions() já tem
// staleTime de 5min (ver hooks/queries/index.ts) — sem isso, cada F5 fazia
// o botão nascer desabilitado por um instante e "ativar sozinho" segundos
// depois, parecendo permissão desatualizada/com bug (bug relatado
// 2026-08-28), quando na verdade era só a primeira busca da sessão ainda
// não ter chegado. Resultado descartado de propósito — só o cache importa.
function PermissionsPrefetchMount() {
  useMyPermissions();
  return null;
}

// Adianta o carregamento do script da Google Maps JS API assim que a sessão
// confirma, em vez de só começar quando a primeira tela que realmente
// precisa dele (Início > Street View do veículo em destaque, mapa,
// Histórico de Rota) monta o próprio <APIProvider>. Bug relatado
// 2026-09-09: "o street view demora muito pra aparecer na tela início" —
// como o PWA agora abre direto no Início ([[gpsdrive_history_deletion_feature_2026-09-08]]),
// esse costumava ser o PRIMEIRO lugar da sessão a precisar do script
// (~500KB+ gzip), pagando o carregamento do zero sem nada em paralelo.
// `loadingStatus` dentro de useGoogleMapsApiLoader (@vis.gl/react-google-maps)
// é uma variável de MÓDULO (compartilhada por todo <APIProvider> desta
// aba, não por instância) — então qualquer outro <APIProvider> montado
// depois (Início, mapa, etc.) só reaproveita o carregamento já em
// andamento/concluído aqui, nunca duplica o script. import() dinâmico
// (não import estático no topo do arquivo) pelo mesmo motivo de
// routes/index.tsx: manter esse pacote fora do bundle inicial de toda
// rota que não usa mapa (login, users, etc.).
function GoogleMapsPreloadMount() {
  const [Provider, setProvider] = useState<React.ComponentType<{ apiKey: string }> | null>(null);
  useEffect(() => {
    let alive = true;
    import("@vis.gl/react-google-maps").then((mod) => {
      if (alive) setProvider(() => mod.APIProvider);
    });
    return () => {
      alive = false;
    };
  }, []);
  const apiKey = (import.meta.env.VITE_GOOGLE_MAPS_API_KEY as string | undefined) ?? "";
  if (!Provider || !apiKey) return null;
  return <Provider apiKey={apiKey} />;
}

// Aviso fixo enquanto o admin está "Logado como" outro usuário (ver
// AuthProvider#impersonate) — só a sessão do GPS Drive Backend troca, o
// Traccar (mapa/rastreamento) continua sendo o do administrador o tempo
// todo. Sempre visível, nunca escondido atrás de um menu, pra nunca
// esquecer que a tela não é a conta do próprio admin.
function ImpersonationBanner() {
  const { impersonating, gpsDriveUser, stopImpersonating } = useAuth();
  if (!impersonating) return null;
  return (
    <div className="fixed inset-x-0 top-0 z-[2000] flex h-9 items-center justify-center gap-3 bg-amber-500 px-3 text-xs font-medium text-amber-950 shadow">
      <span>
        Você está logado como <strong>{gpsDriveUser?.name}</strong>
      </span>
      <button
        type="button"
        onClick={() => void stopImpersonating()}
        className="rounded bg-amber-950/10 px-2 py-0.5 font-semibold hover:bg-amber-950/20"
      >
        Voltar para administrador
      </button>
    </div>
  );
}

// Funcionário (pedido 2026-10-09): menus escolhidos em Permissões. Se ele
// cair numa tela cujo menu está desligado (o login sempre abre o Início;
// URL digitada; link antigo), vai pro primeiro menu liberado — mesma regra
// da sidebar (useSidebarNavItems), nunca uma lista paralela. Só decide
// depois das permissões carregarem, pra não expulsar ninguém no boot.
const MENU_GATED_URLS: string[] = ["/inicio", "/", "/geofences", "/reports", "/camera", "/agenda"];

function MenuAccessRedirect() {
  const { gpsDriveUser } = useAuth();
  const { data: permissions } = useMyPermissions();
  const { data: myCompany } = useMyCompany();
  const navItems = useSidebarNavItems();
  const pathname = useRouterState({ select: (r) => r.location.pathname });
  const navigate = useNavigate();
  const isFuncionario = gpsDriveUser?.accountType === "funcionario";
  useEffect(() => {
    if (!isFuncionario || !permissions || !myCompany) return;
    const matches = (url: string) => (url === "/" ? pathname === "/" : pathname === url || pathname.startsWith(`${url}/`));
    // Só os menus decididos por Menus do sistema/Agenda — Financeiro,
    // Vendas etc. dependem de consultas próprias que podem ainda estar
    // carregando (a própria tela já mostra "Sem permissão").
    const menu = SIDEBAR_ITEMS.find((item) => MENU_GATED_URLS.includes(item.url) && matches(item.url));
    if (!menu || navItems.some((item) => item.url === menu.url)) return;
    const target = navItems[0];
    if (target) navigate({ to: target.url, replace: true });
  }, [isFuncionario, permissions, myCompany, navItems, pathname, navigate]);
  return null;
}

// Precisa ser um componente próprio (não inline em RootComponent) porque
// useAuth() só funciona DENTRO de <AuthProvider> — RootComponent é quem
// declara o Provider, então o consumo do contexto (aqui, `impersonating`
// pro espaçamento do banner) tem que morrer num descendente dele.
function AuthenticatedShell() {
  const { impersonating, gpsDriveStatus, gpsDriveUser } = useAuth();
  // Funcionário no celular: só Agenda/Usuários/Vendas (pedido 2026-10-09),
  // ver FuncionarioMobileShell.
  const isFuncionario = gpsDriveUser?.accountType === "funcionario";
  // Bug real 2026-09-01: estes dois montavam incondicionalmente assim que
  // o Traccar autenticava (`user` truthy em AuthGate), mas gpsDriveStatus
  // ainda começa como "loading" nesse instante — o access token do GPS
  // Drive Backend só existe depois que login()/recoverSession() (ver
  // auth-context.tsx) resolve, alguns milissegundos DEPOIS de setUser(u).
  // Resultado: GET /users/me/permissions e /admin/branding sempre saíam
  // com Authorization vazio no primeiríssimo carregamento, tomavam 401,
  // e o retry-once de fetchWithAuth (gpsdrive-client.ts) disparava um
  // refreshGpsDriveSession() CONCORRENTE com o próprio login() em
  // andamento — se esse refresh paralelo falhasse por qualquer motivo
  // transitório, ele sobrescrevia gpsDriveStatus para "expired" por cima
  // do "ok" que o login() estava prestes a (ou já tinha acabado de)
  // setar, deixando o aviso "Sessão de gestão expirada" preso mesmo com
  // login bem-sucedido. Esperar o próprio login/recuperação de sessão
  // decidir "ok"/"expired" primeiro elimina a corrida — nenhuma chamada
  // sai mais com token vazio. NUNCA condiciona o resto do shell abaixo
  // (mapa/sidebar/PWA) a isso — só estes dois prefetches esperam; o
  // rastreamento continua aparecendo na hora mesmo com o GPS Drive Backend
  // lento/fora do ar, que é o comportamento que login() já garante.
  const gpsDriveReady = gpsDriveStatus !== "loading";
  const isMobileViewport = useIsMobileViewport();
  return (
    <>
      {gpsDriveReady && <BrandingThemeMount />}
      {gpsDriveReady && <PermissionsPrefetchMount />}
      {gpsDriveReady && <UpdateAvailableNotice />}
      <ConnectionLostBanner />
      {gpsDriveReady && <AppStoreUpdatePrompt />}
      <GoogleMapsPreloadMount />
      <RouteProgressBar />
      <ImpersonationBanner />
      {/* Mobile PWA app (< md) */}
      {/* Só monta a versão que aparece (2026-10-05, "F5 demora muito"):
          antes as duas eram montadas e uma só escondida por CSS — no
          celular o mapa/lista do desktop inteiro renderizava invisível, no
          computador o app inteiro do celular, dobrando o tempo até sair o
          loading. Este shell só existe no cliente (o SSR sempre para no
          GpsBootLoader do AuthGate), então ler a largura aqui é seguro. */}
      {isMobileViewport && isFuncionario ? (
        <div className={cn("md:hidden", impersonating && "pt-9")}>
          <FuncionarioMobileShell>
            <Outlet />
          </FuncionarioMobileShell>
        </div>
      ) : isMobileViewport ? (
        <div className={cn("md:hidden", impersonating && "pt-9")}>
          <MobileApp />
        </div>
      ) : (
        <div
          className={cn(
            "hidden w-full overflow-hidden bg-background text-foreground md:flex",
            impersonating ? "mt-9 h-[calc(100vh-2.25rem)]" : "h-screen",
          )}
        >
          <AppSidebar />
          {gpsDriveReady && <MenuAccessRedirect />}
          <main className="flex-1 overflow-auto">
            <Outlet />
          </main>
        </div>
      )}
    </>
  );
}

// Mesmo corte do Tailwind `md` (768px) usado nas classes md:hidden/md:flex acima.
const MOBILE_VIEWPORT_QUERY = "(max-width: 767px)";

function useIsMobileViewport() {
  const [isMobile, setIsMobile] = useState(
    () => typeof window !== "undefined" && window.matchMedia(MOBILE_VIEWPORT_QUERY).matches,
  );
  useEffect(() => {
    const mql = window.matchMedia(MOBILE_VIEWPORT_QUERY);
    const onChange = () => setIsMobile(mql.matches);
    onChange();
    mql.addEventListener("change", onChange);
    return () => mql.removeEventListener("change", onChange);
  }, []);
  return isMobile;
}

function NotFoundComponent() {
  return (
    <div className="flex min-h-screen items-center justify-center bg-background px-4">
      <div className="max-w-md text-center">
        <h1 className="text-7xl font-bold">404</h1>
        <p className="mt-2 text-muted-foreground">Página não encontrada.</p>
      </div>
    </div>
  );
}

// Arquivo JS de uma tela que não carregou (deploy novo com a aba antiga
// aberta, ou a rede oscilou no meio). O navegador guarda a falha do import()
// até a página recarregar — tentar de novo sem recarregar nunca resolve.
const CHUNK_ERROR = /dynamically imported module|Importing a module script failed|error loading dynamically|reading 'component'/i;
const CHUNK_RELOADS_KEY = "gd_chunk_reloads";

// Print 2026-10-20 ("toda hora dando esse erro na agenda"): antes era 1
// recarga por mensagem na sessão — com vários deploys no dia, da 2ª vez em
// diante sobrava a tela de erro. Agora recarrega sempre, com trava contra
// loop: no máximo 3 recargas em 5 minutos.
function claimChunkReload(): boolean {
  try {
    const now = Date.now();
    const recent = (JSON.parse(sessionStorage.getItem(CHUNK_RELOADS_KEY) ?? "[]") as number[]).filter(
      (t) => now - t < 5 * 60_000,
    );
    if (recent.length >= 3) return false;
    sessionStorage.setItem(CHUNK_RELOADS_KEY, JSON.stringify([...recent, now]));
    return true;
  } catch {
    return false; // sem sessionStorage não dá pra garantir que não entra em loop
  }
}

function ErrorComponent({ error, reset }: { error: Error; reset: () => void }) {
  const router = useRouter();
  const isChunkError = CHUNK_ERROR.test(error?.message ?? "");
  const [reloading, setReloading] = useState(false);
  useEffect(() => {
    reportLovableError(error, { boundary: "tanstack_root_error_component" });
    if (isChunkError && claimChunkReload()) {
      setReloading(true);
      window.location.reload();
    }
  }, [error, isChunkError]);
  if (reloading) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-background px-4">
        <div className="flex flex-col items-center gap-3 text-center">
          <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
          <p className="text-sm text-muted-foreground">Atualizando o sistema…</p>
        </div>
      </div>
    );
  }
  return (
    <div className="flex min-h-screen items-center justify-center bg-background px-4">
      <div className="max-w-md text-center">
        <h1 className="text-xl font-semibold">{isChunkError ? "Não foi possível carregar esta tela" : "Erro ao carregar"}</h1>
        <p className="mt-2 text-sm text-muted-foreground">
          {isChunkError ? "Verifique a internet e toque em Recarregar." : error.message}
        </p>
        <button
          onClick={() => {
            if (isChunkError) {
              window.location.reload();
              return;
            }
            router.invalidate();
            reset();
          }}
          className="mt-4 inline-flex items-center rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground hover:bg-primary/90"
        >
          {isChunkError ? "Recarregar" : "Tentar novamente"}
        </button>
      </div>
    </div>
  );
}

export const Route = createRootRouteWithContext<{ queryClient: QueryClient }>()({
  head: () => ({
    meta: [
      { charSet: "utf-8" },
      // viewport-fit=cover é obrigatório pro iOS resolver env(safe-area-inset-*)
      // (sem isso os valores ficam 0 sempre) — necessário pra barra inferior
      // do PWA (mobile-app.tsx) não ficar cortada pela faixa de gesto/home
      // indicator (bug relatado 2026-08-31: "fica cortado a barra do menu e
      // fica aparecendo o mapa no fundo").
      { name: "viewport", content: "width=device-width, initial-scale=1, viewport-fit=cover" },
      { title: "Mapa — Wp Rastreamento" },
      {
        name: "description",
        content: "Sistema moderno de rastreamento e monitoramento de frota em tempo real.",
      },
      { property: "og:title", content: "Mapa — Wp Rastreamento" },
      {
        property: "og:description",
        content: "Sistema moderno de rastreamento e monitoramento de frota em tempo real.",
      },
      { property: "og:type", content: "website" },
      { name: "twitter:card", content: "summary_large_image" },
      { name: "twitter:title", content: "Mapa — Wp Rastreamento" },
      {
        name: "twitter:description",
        content: "Sistema moderno de rastreamento e monitoramento de frota em tempo real.",
      },
      {
        property: "og:image",
        content:
          "https://pub-bb2e103a32db4e198524a2e9ed8f35b4.r2.dev/19e256b8-0185-4d35-9910-ed6f4d362cd7/id-preview-671c4f3f--08095a77-1ee2-43eb-a056-e04547e15531.lovable.app-1783342628853.png",
      },
      {
        name: "twitter:image",
        content:
          "https://pub-bb2e103a32db4e198524a2e9ed8f35b4.r2.dev/19e256b8-0185-4d35-9910-ed6f4d362cd7/id-preview-671c4f3f--08095a77-1ee2-43eb-a056-e04547e15531.lovable.app-1783342628853.png",
      },
    ],
    links: [
      { rel: "stylesheet", href: appCss },
      // Leaflet/Geoman/MarkerCluster CSS saíram daqui (2026-09-23): vinham do
      // unpkg.com como <link> bloqueante no <head> de TODA tela, mas o mapa
      // principal já é deck.gl/Google Maps — só Cercas ainda usa Leaflet, e
      // importa o CSS sozinha (ver geofences.tsx). Uma CDN externa lenta
      // deixava o app inteiro em tela preta até responder.
      // Google Fonts saiu daqui também (2026-10-05) pelo mesmo motivo — a
      // Plus Jakarta Sans agora é servida por nós (@font-face em styles.css).
      // Sem <link rel="icon"> fixo (2026-10-29): o HeadContent recriava este
      // link a cada troca de rota e o favicon voltava pro da GPS Drive em
      // qualquer empresa. Quem cria/atualiza o ícone é use-branding-theme.
      { rel: "manifest", href: "/manifest.webmanifest" },
    ],
  }),
  shellComponent: RootShell,
  component: RootComponent,
  notFoundComponent: NotFoundComponent,
  errorComponent: ErrorComponent,
});

// Aplica a cor da marca já salva no localStorage (ver useBrandingTheme)
// de forma síncrona, ANTES do <head> terminar de carregar — elimina o
// flash de azul padrão -> cor da empresa no F5/entrar no sistema (bug
// relatado 2026-08-28). Mesmas custom properties/fórmula color-mix() do
// hook; só roda uma vez por carregamento de página, puro DOM, sem React.
const BRANDING_COLOR_BOOT_SCRIPT = `(function(){try{var c=localStorage.getItem("gpsdrive:branding-color");if(!c)return;var s=document.documentElement.style;s.setProperty("--primary",c);s.setProperty("--ring",c);s.setProperty("--sidebar-primary",c);s.setProperty("--sidebar-ring",c);s.setProperty("--accent","color-mix(in srgb, "+c+" 15%, transparent)");s.setProperty("--accent-foreground",c);s.setProperty("--sidebar-accent","color-mix(in srgb, "+c+" 18%, transparent)");s.setProperty("--sidebar-accent-foreground",c);}catch(e){}})();`;

// TENTATIVA REVERTIDA 2026-09-02: um disjuntor baseado em taxa de commits do
// React (window.location.reload() se a taxa cruzasse um teto) chegou a
// existir aqui pra mitigar o loop de re-render descrito na memória
// gpsdrive_react_error_185_router_stuck — mas o teto foi calibrado contra
// uma conta de teste com ZERO veículos, nunca testado contra a frota real
// de 5000, e disparava falso-positivo em produção (usuário real relatou a
// tela "piscando e recarregando sozinha o tempo todo" logo depois do
// deploy). Revertido imediatamente. Qualquer nova tentativa de watchdog
// baseado em taxa de render PRECISA ser calibrada contra a frota real
// (5000 veículos, sessão autenticada de verdade) antes de ir pra produção,
// não só contra um cenário sintético pequeno.
function RootShell({ children }: { children: ReactNode }) {
  return (
    <html lang="pt-BR">
      <head>
        <HeadContent />
        <script dangerouslySetInnerHTML={{ __html: BRANDING_COLOR_BOOT_SCRIPT }} />
      </head>
      <body>
        {children}
        <Scripts />
      </body>
    </html>
  );
}

// Pedido 2026-09-03: "eu dou F5 já aparece tudo carregado igual" — cache do
// React Query (identity/permissões/avatar/perfil/faturas/documentos/fotos
// de veículo etc.) sobrevive ao F5 via localStorage (ver
// lib/query-persister.ts), restaurado ANTES do primeiro render de cada
// tela. Continua marcado como stale de qualquer forma (mesmo critério do
// `initialDataUpdatedAt: 0` já usado em useVehicleImages/avatarCacheKey) —
// sempre revalida contra o servidor por trás, só não deixa a tela "vazia
// populando aos poucos" enquanto isso acontece. Exclui "vehicles"/
// "positions" da frota: teste de carga já mostrou frotas de 5000+
// dispositivos (ver comentário em routes/index.tsx) — persistir isso
// sincronamente no localStorage a cada mudança arriscaria estourar a cota
// do navegador e travar a aba; a frota já é sempre buscada de novo na hora
// mesmo (tempo real via WebSocket), não precisa desse "carregado igual no
// F5".
function RootComponent() {
  const { queryClient } = Route.useRouteContext();
  const pathname = useRouterState({ select: (r) => r.location.pathname });
  const isAuthRoute = pathname === "/login";
  // Cache local dos JS/CSS com hash — abertura rápida em rede lenta (ver lib/asset-cache.ts).
  useEffect(() => {
    registerAssetCache();
  }, []);
  // Painel Super Master (/master/*) — sessão de PlatformAdmin, inteiramente
  // separada da sessão de empresa (Traccar + GPS Drive Backend) que o
  // resto deste componente gerencia. Nunca monta AuthProvider/AuthGate
  // aqui: um PlatformAdmin não tem (nem pode ter) sessão Traccar, então o
  // redirect padrão para /login (ver AuthGate abaixo) nunca deve se aplicar
  // a estas rotas.
  const isPlatformRoute = pathname.startsWith("/master");
  // Política de Privacidade — precisa ser acessível publicamente, sem
  // sessão, para o link exigido no cadastro do app na Play Store Console
  // (o Google acessa a URL sem estar logado). Não monta AuthProvider nem
  // o PersistQueryClientProvider: a página é 100% estática, sem hooks de
  // dados, então não depende de nada disso.
  // Wizard de assinatura de contrato por autoatendimento (2026-09-11,
  // /contrato/assinar/:token, ver contrato.assinar.$token.tsx) — mesmo
  // motivo de /privacidade: o cliente nunca tem sessão GPS Drive aqui, e
  // AuthGate redirecionaria pra /login sem isso (nenhum `user` presente).
  // A própria página busca os dados direto pelo service (sem React Query),
  // então não precisa do PersistQueryClientProvider — mesmo bypass total de
  // /privacidade serve aqui.
  // Compartilhar localização (2026-09-23, /ao-vivo/:token, ver
  // ao-vivo.$token.tsx) — convidado nunca tem sessão, mesmo bypass.
  const isPublicStaticRoute =
    pathname === "/privacidade" ||
    pathname === "/politica-de-privacidade" ||
    pathname.startsWith("/contrato/assinar/") ||
    pathname.startsWith("/ao-vivo/") ||
    // Assistência (2026-10-16): tela do guincheiro e do cliente acompanhando.
    pathname.startsWith("/guincho/") ||
    pathname.startsWith("/acompanhar/") ||
    // Link de indicação (2026-09-27, indicacao.$code.tsx) — indicado sem sessão.
    pathname.startsWith("/indicacao/") ||
    // Formulário público da empresa (2026-10-05, f.$slug.tsx) — sem sessão.
    pathname.startsWith("/f/") ||
    // Vistoria por link público (2026-10-23, vistoria.tsx) — motorista sem conta.
    pathname === "/vistoria" ||
    // Apresentação comercial (2026-10-29, apresentacao.tsx) — página aberta.
    pathname === "/apresentacao" ||
    // App do técnico (2026-10-07, tecnico.tsx) — sessão própria do técnico
    // da Agenda, nunca a sessão de empresa.
    pathname === "/tecnico" ||
    pathname.startsWith("/tecnico/");

  if (isPublicStaticRoute) {
    return <Outlet />;
  }
  // Link digitado com acento (/apresentação) cai na rota sem acento.
  if (typeof window !== "undefined" && /^\/apresenta(ç|%C3%A7)(ã|%C3%A3)o\/?$/i.test(pathname)) {
    window.location.replace("/apresentacao");
    return null;
  }

  // Módulo Agenda (/agenda/*, 2026-09-13) — MESMA sessão de empresa
  // (AuthProvider/AuthGate abaixo, Traccar + GPS Drive Backend), só o
  // SHELL visual é diferente: cada rota agenda.*.tsx já se envolve com
  // AgendaShell (components/agenda/agenda-shell.tsx), então aqui só evita
  // o AppSidebar/AuthenticatedShell do app principal por cima. Nunca monta
  // um Provider de auth separado — diferente de isPlatformRoute abaixo,
  // que é uma sessão de PlatformAdmin genuinamente à parte.
  const isAgendaRoute = pathname.startsWith("/agenda");

  const persistOptions = {
    persister: gpsDrivePersister,
    maxAge: 1000 * 60 * 60 * 24,
    // Descarta qualquer cache salvo ANTES desta versão — bug real
    // 2026-09-03: useVehicleImages/useVehicleMetadataSummaries
    // devolviam um Map, que vira "{}" ao passar por JSON.stringify (só
    // depois convertidos pra objeto plano, ver hooks/queries/index.ts).
    // Sem trocar o buster, quem já tinha aberto o app entre a ativação
    // da persistência e este fix ficaria com esse "{}" preso no
    // localStorage até expirar sozinho (maxAge 24h) ou ser sobrescrito.
    buster: "v2-2026-09-03-fix-map-serialization",
    dehydrateOptions: {
      shouldDehydrateQuery: (query: Parameters<typeof defaultShouldDehydrateQuery>[0]) => {
        const [firstKey] = query.queryKey as unknown[];
        if (firstKey === "vehicles" || firstKey === "positions") return false;
        // Financeiro (2026-09-28): valores/devedores da empresa nunca ficam
        // salvos no navegador — sempre buscados de novo.
        if (firstKey === "finance") return false;
        // Assistência (2026-10-16): guinchos/solicitações sempre frescos.
        if (firstKey === "assistance-center") return false;
        // Índice de busca (nome/CPF de ~3.400 clientes) — grande e pessoal
        // demais pra ficar no localStorage; recarrega do servidor.
        if (firstKey === "vehicle-search-index") return false;
        // Gestão de Frota: dados de motorista/locatário não ficam no navegador.
        if (firstKey === "fleet-mgmt") return false;
        // Consultas (CPF/antecedentes): dado pessoal, nunca no navegador.
        if (firstKey === "consultations") return false;
        // Vendas/pré-contratos (CPF, endereço de quem preencheu o formulário).
        if (firstKey === "sales") return false;
        // Vistorias: fotos com link assinado que vence — sempre do servidor.
        if (firstKey === "vehicle-inspections") return false;
        return defaultShouldDehydrateQuery(query);
      },
      // Nunca persiste mutation em voo — não faz sentido "replayar" um
      // PUT/POST pendente depois de um F5.
      shouldDehydrateMutation: () => false,
    },
  };

  if (isPlatformRoute) {
    return (
      <PersistQueryClientProvider client={queryClient} persistOptions={persistOptions}>
        <PlatformAuthProvider>
          <Outlet />
          <Toaster />
        </PlatformAuthProvider>
      </PersistQueryClientProvider>
    );
  }

  return (
    <PersistQueryClientProvider client={queryClient} persistOptions={persistOptions}>
      <AuthProvider>
        <AuthGate isAuthRoute={isAuthRoute}>
          {isAuthRoute ? <Outlet /> : isAgendaRoute ? <AgendaRouteShell /> : <AuthenticatedShell />}
        </AuthGate>
        <Toaster />
      </AuthProvider>
    </PersistQueryClientProvider>
  );
}

// Agenda tem casca própria (AgendaShell), mas o funcionário no celular
// continua com a barra Agenda/Usuários/Vendas por baixo (2026-10-09) —
// antes, tocar em Agenda sumia com a barra e ele ficava preso lá.
function AgendaRouteShell() {
  const { gpsDriveUser } = useAuth();
  const isMobileViewport = useIsMobileViewport();
  if (isMobileViewport && gpsDriveUser?.accountType === "funcionario") {
    return (
      <FuncionarioMobileShell>
        <Outlet />
      </FuncionarioMobileShell>
    );
  }
  return <Outlet />;
}

// Abertura com o Porsche (components/boot-intro.tsx) removida 2026-10-29
// a pedido ("entra direto, bem mais rápido") — ~2,4 s a menos em todo F5.

// Exige sessão válida (via AuthContext) em toda rota exceto /login. Sem
// sessão, redireciona para /login; enquanto a sessão inicial ainda está
// sendo verificada, evita renderizar o conteúdo protegido.
function AuthGate({ isAuthRoute, children }: { isAuthRoute: boolean; children: ReactNode }) {
  const { user, loading, gpsDriveUser, impersonating } = useAuth();
  const navigate = useNavigate();
  const router = useRouter();
  useRealtimeSync(!isAuthRoute && !!user);
  useLiveSync(!isAuthRoute && !!user);
  useNotificationSoundPlayer(!isAuthRoute && !!user, user?.id);
  useNativePushRegistration(!isAuthRoute && !!user);
  useNativeNotificationActions(!isAuthRoute && !!user);

  useEffect(() => {
    if (isAuthRoute || loading || user) return;
    // Não disparar `navigate` enquanto o router JÁ está no meio de outra
    // transição (ex.: acabou de navegar de /login pra cá) — causa real do
    // "Minified React error #185"/tela branca relatado 2026-09-02: essa
    // navegação daqui colidindo com a navegação login->mapa (login.tsx faz
    // `navigate({to:"/"})` logo após o login) deixava o router preso — os
    // componentes internos do TanStack Router entram num loop de re-render
    // que só cresce até estourar o limite do React. Confirmado com
    // reprodução real (Puppeteer contra produção): o router fica com
    // `resolvedLocation` travado atrás de `location`, sinal de que uma
    // navegação nunca terminou de commitar por causa dessa colisão. Não
    // disparar aqui enquanto outra já está em voo evita a colisão na
    // origem — muito mais seguro que tentar detectar/recuperar do loop
    // depois que ele já começou (tentativa anterior, revertida, baseada em
    // taxa de re-render, não calibrada direito e que chegou a causar reload
    // em loop pra usuário real). O efeito roda de novo (deps abaixo) assim
    // que o router se estabilizar, então o redirect real ainda acontece
    // normalmente se `user` continuar ausente depois.
    if (router.state.isLoading) return;
    // Técnico da Agenda logado pelo mesmo login (2026-10-08): sem sessão de
    // empresa, abre o app do técnico em vez da tela de login.
    navigate({ to: getTechnicianToken() ? "/tecnico" : "/login", replace: true });
  }, [isAuthRoute, loading, user, navigate, router]);

  if (isAuthRoute) return <>{children}</>;

  if (loading || !user) {
    // Indicador visível em vez de só um texto cinza pequeno: no PWA/app (tema
    // escuro) isso parecia "tela preta" pros clientes enquanto o app
    // carregava em rede lenta (2026-09-24). Desde 2026-10-04 é a estrada com carro do
    // GpsBootLoader, que fica invisível nos primeiros 400 ms — F5 com sessão
    // rápida não mostra loading nenhum.
    return (
      <GpsBootLoader />
    );
  }

  // Admin/funcionário com senha fraca: nada do app até trocar (o backend
  // também bloqueia). Nunca durante "Logar como" — quem está vendo é o
  // administrador, não o dono da conta.
  if (gpsDriveUser?.mustChangePassword && !impersonating) return <ForcePasswordChange />;

  return <>{children}</>;
}
